import BigNumber from 'bignumber.js';
import { z } from 'zod';
import { DocumentType } from '~/types/document';
import { BookkeepingEntrySource } from '~/types/general-bookkeeping';
import { requiresAdjustment } from '~/utils/bookkeeping/adjustments';

const moneySchema = z.object({
  amount: z.string().min(1),
  currency: z.string(),
});

const BookkeepingEntrySchema = z
  .object({
    id: z.string().nullable(),
    bookkeepingAccountId: z.string().min(1),
    counterpart: z
      .object({
        id: z.string(),
        companyName: z.string().nullable(),
      })
      .nullable(),
    documentType: z.nativeEnum(DocumentType).nullable(),
    documentNumber: z.string().nullable(),
    documentId: z.string().nullable(),
    flowId: z.string().nullable(),
    description: z.string().optional(),
    dare: moneySchema.nullable(),
    avere: moneySchema.nullable(),
    readOnlyAccount: z.boolean().optional(),
    readOnlyAmount: z.boolean().optional(),
    source: z.nativeEnum(BookkeepingEntrySource),
    competencePeriodStart: z.date().nullable(),
    competencePeriodEnd: z.date().nullable(),
    adjustmentAccountId: z.string().nullable().optional(),
  })
  .refine(
    (data) => {
      // Ensure entry has either dare or avere, but not both
      const hasDare = data.dare?.amount && parseFloat(data.dare.amount) > 0;
      const hasAvere = data.avere?.amount && parseFloat(data.avere.amount) > 0;
      return (hasDare && !hasAvere) || (!hasDare && hasAvere);
    },
    {
      params: { issue: 'entry_must_have_either_dare_or_avere' },
      path: ['dare'],
    },
  );

export type BookkeepingEntryFormInput = z.input<typeof BookkeepingEntrySchema>;
export type BookkeepingEntryFormOutput = z.output<typeof BookkeepingEntrySchema>;

export const BOOKKEEPINGTRANSACTIONNOTBALANCEDERRORID = 'transaction_not_balanced';

export const BookkeepingTransactionSchema = z
  .object({
    date: z.date(),
    description: z.string().min(1),
    entries: z.array(BookkeepingEntrySchema).min(2),
    recommendedReconciliationFlowId: z.string().nullish(),
  })
  .refine(
    ({ entries }) => {
      const difference = entries.reduce(
        (prev, curr) => prev.plus(curr.dare?.amount ?? 0).minus(curr.avere?.amount ?? 0),
        BigNumber(0),
      );

      return difference.isZero();
    },
    {
      message: 'transaction_not_balanced',
      params: { id: BOOKKEEPINGTRANSACTIONNOTBALANCEDERRORID, issue: 'transaction_not_balanced' },
      path: ['entries'],
    },
  )
  .superRefine(({ date, entries }, ctx) => {
    entries.forEach((entry, idx) => {
      if (
        requiresAdjustment(date, entry.competencePeriodStart, entry.competencePeriodEnd) &&
        !entry.adjustmentAccountId
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          params: { issue: 'required' },
          path: ['entries', idx, 'adjustmentAccountId'],
        });
      }
    });
  });

export type BookkeepingTransactionFormInput = z.input<typeof BookkeepingTransactionSchema>;
export type BookkeepingTransactionFormOutput = z.output<typeof BookkeepingTransactionSchema>;
